/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React from 'react';
import { BookOpen, FileCode2, ExternalLink, Book, Zap, Shield, Webhook } from 'lucide-react';

const docs = [
  {
    icon: BookOpen,
    title: 'Partner Integration Guide',
    description: 'End-to-end integration guide covering concepts, workflows, assignments, webhooks, and field reference.',
    href: '/docs/partner-guide-v3.html',
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
  {
    icon: FileCode2,
    title: 'API Reference — Swagger',
    description: 'Interactive specification for all Send2Me endpoints with request/response schemas.',
    href: '/docs/swagger.html',
    color: 'text-success',
    bg: 'bg-success/10',
  },
];

const quickLinks = [
  { icon: Zap, label: 'Quick Start', href: '/docs/partner-guide-v3.html#quickstart' },
  { icon: Book, label: 'Workflow', href: '/docs/partner-guide-v3.html#workflows' },
  { icon: Book, label: 'Assignment', href: '/docs/partner-guide-v3.html#assignments' },
  { icon: Webhook, label: 'Webhook Events', href: '/docs/partner-guide-v3.html#webhooks' },
  { icon: Shield, label: 'Authentication', href: '/docs/partner-guide-v3.html#authentication' },
  { icon: FileCode2, label: 'All API Endpoints', href: '/docs/swagger.html' },
];

const Help = () => {
  return (
    <div className="space-y-4 max-w-[720px]">
      {/* Header — matches Dashboard/Settings/Assignments pattern */}
      <div className="mb-4">
        <h1 className="page-title">Help &amp; Documentation</h1>
        <p className="page-subtitle">Integration guides and API reference for Send2Me.</p>
      </div>

      {/* Main doc cards — using card-premium glass-panel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {docs.map(({ icon: Icon, title, description, href, color, bg }) => (
          <a
            key={href}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="card-premium rounded-xl group flex flex-col gap-4 hover:border-primary/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-200"
          >
            <div className="flex items-start justify-between">
              <div className={`w-9 h-9 rounded-md ${bg} border border-base-content/10 flex items-center justify-center flex-shrink-0`}>
                <Icon size={18} className={color} />
              </div>
              <ExternalLink size={14} className="text-base-content/20 group-hover:text-primary transition-colors mt-1" />
            </div>

            <div>
              <h2 className="text-sm font-black text-base-content uppercase tracking-tight mb-1">{title}</h2>
              <p className="text-xs text-base-content/50 leading-relaxed font-semibold">{description}</p>
            </div>
          </a>
        ))}
      </div>

      {/* Quick Links */}
      <div className="card-premium rounded-xl glass-panel border border-base-content/10">
        <h2 className="text-sm font-black uppercase tracking-tight text-base-content mb-4">Quick Links</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {quickLinks.map(({ icon: Icon, label, href }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl border border-base-content/10 bg-base-200/50 hover:border-primary/40 hover:bg-primary/5 hover:text-primary text-base-content/60 transition-all duration-150 group"
            >
              <Icon size={14} className="flex-shrink-0 group-hover:text-primary transition-colors" />
              <span className="truncate text-xs font-black uppercase tracking-wide">{label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Help;
